import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api, apiErr } from "../api";
import { useAdminData } from "../useAdminData";
import { Panel, Badge, Button, PageHeader, Spinner, fmtNum, fmtDate } from "../Primitives";
import { TOTAL_GAMES, TOTAL_PLATFORMS, TOTAL_PROVIDERS } from "../catalog";
import { LogOut } from "lucide-react";

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[var(--border)] last:border-0 text-[13px]">
      <span className="text-[var(--muted)]">{k}</span><span className="mono text-right">{v}</span>
    </div>
  );
}

function DataSettings() {
  const { data } = useAdminData(async (a) => {
    const [s, st] = await Promise.all([a.get("/admin/data/settings"), a.get("/admin/data/stats")]);
    return { settings: s.data, stats: st.data };
  }, []);
  if (!data) return <div className="p-4"><Spinner /></div>;
  const st = data.stats;
  return (
    <div className="px-4 pb-4" data-testid="data-settings">
      <Row k="Eventos armazenados" v={fmtNum(st.events)} />
      <Row k="Evento mais antigo" v={fmtDate(st.oldest_event)} />
      <Row k="Sessões · visitantes" v={`${fmtNum(st.sessions)} · ${fmtNum(st.visitors)}`} />
      <Row k="Linhas de resumo diário" v={fmtNum(st.daily_stats)} />
      <Row k="Último resumo automático" v={fmtDate(data.settings.last_rollup_at)} />
      <p className="ops-help mt-3">Os eventos não são excluídos automaticamente. Resumos diários são gerados a cada hora.</p>
    </div>
  );
}

function ChangePassword() {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setMsg(null);
    if (f.next.length < 10) return setMsg({ bad: true, text: "A nova senha deve ter pelo menos 10 caracteres." });
    if (f.next !== f.confirm) return setMsg({ bad: true, text: "As senhas não coincidem." });
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      setF({ current: "", next: "", confirm: "" });
      setMsg({ bad: false, text: "Senha alterada. Outras sessões foram encerradas." });
    } catch (err) { setMsg({ bad: true, text: apiErr(err) }); } finally { setBusy(false); }
  };
  const input = (k, label, ac) => (
    <label className="block space-y-1"><span className="block ops-label">{label}</span>
      <input type="password" autoComplete={ac} value={f[k]} onChange={set(k)} required className="mia-input" data-testid={`change-pw-${k}`} /></label>
  );
  return (
    <form onSubmit={submit} className="px-4 pb-4 grid gap-3 text-[13px]" data-testid="change-password-form">
      {input("current", "Senha atual", "current-password")}
      {input("next", "Nova senha (mín. 10 caracteres)", "new-password")}
      {input("confirm", "Confirmar nova senha", "new-password")}
      <div><Button variant="outline" size="sm" type="submit" disabled={busy} data-testid="change-pw-submit">{busy ? <Spinner size={12} /> : "Alterar senha"}</Button></div>
      {msg && <p role="status" data-testid="change-pw-msg" style={{ color: msg.bad ? "var(--bad)" : "var(--ok)" }}>{msg.text}</p>}
    </form>
  );
}

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div>
      <PageHeader title="Configurações" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Conta">
          <div className="px-4 pb-4">
            <Row k="Nome" v={user?.name || "—"} />
            <Row k="E-mail" v={user?.email} />
            <Row k="Perfil" v={<Badge tone="accent">{user?.role_label}</Badge>} />
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="settings-logout" className="mt-4"><LogOut size={13} /> Encerrar sessão</Button>
          </div>
        </Panel>
        <Panel title="Segurança">
          <div className="px-4 pb-4">
            <Row k="Autenticação" v="JWT em cookie httpOnly" />
            <Row k="Autorização" v="Por perfil, verificada no servidor" />
            <Row k="Senhas" v="bcrypt" />
            <Row k="Força bruta" v="5 por IP · 10 por conta → 15 min" />
            <Row k="Recuperação de senha" v="Link de uso único gerado no servidor" />
          </div>
        </Panel>
        <Panel title="Trocar senha"><ChangePassword /></Panel>
        <Panel title="Dados e retenção"><DataSettings /></Panel>
        <Panel title="Catálogo">
          <div className="px-4 pb-4">
            <Row k="Jogos" v={TOTAL_GAMES} />
            <Row k="Plataformas" v={TOTAL_PLATFORMS} />
            <Row k="Provedores" v={TOTAL_PROVIDERS} />
            <p className="ops-help mt-3">Somente leitura. A fonte do catálogo é o código do site público.</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
